import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';

import api from 'core/api';
import manager from 'core/user/manager';
import useIsCallbackRoute from 'core/user/useIsCallbackRoute';
import useMount from 'utils/hooks/useMount';

const context = createContext({
  user: null,
  isLoading: false,
  hasError: false,
  hasAccess: null,
  reset: _.noop
});

export const useUser = () => useContext(context);

const getUserInfo = () => api.get('/users/me');

export const UserProvider = ({ children }) => {
  const isCallback = useIsCallbackRoute();

  const [isLoading, setIsLoading] = useState(!isCallback);
  const [user, setUser] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [hasSessionExpired, setHasSessionExpired] = useState(false);
  const [hasAccess, setHasAccess] = useState(null);

  useMount(() => {
    const handleInitialize = async () => {
      const oidcUserDetails = await manager.getUser();

      if (!oidcUserDetails) {
        setIsLoading(false);
        return;
      }

      api.setBearerToken(oidcUserDetails.id_token);

      try {
        const userDetails = await getUserInfo();

        setUser(userDetails);
        setHasError(false);
        setHasSessionExpired(false);
        setHasAccess(true);
      } catch (e) {
        switch (e.response?.status) {
          case 403:
            setHasAccess(false);
            break;

          case 401:
            setHasSessionExpired(true);
            break;

          default:
            setHasError(true);
            break;
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (!isCallback) {
      void handleInitialize();
    }
  });

  const handleReset = useCallback(() => {
    setUser(null);
    setHasError(false);
    setHasSessionExpired(false);
    setHasAccess(null);
    setIsLoading(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      hasError,
      hasAccess,
      hasSessionExpired,
      reset: handleReset
    }),
    [user, isLoading, hasError, hasAccess, hasSessionExpired, handleReset]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
